<script setup lang="ts">
	import { ref, onMounted, onBeforeUnmount, computed, toRef } from "vue";
	import { useDisplay } from "vuetify";
	import SeanIcon from "@/components/icons/SeanIcon.vue";
	import RunAgentDialog from "@/components/scribe/dialogs/RunAgentDialog.vue";
	import LuckyPackPopup from "@/components/scribe/LuckyPackPopup.vue";
	import SparkGuidesWizard from "@/components/scribe/SparkGuidesWizard.vue";
	import UnreviewedSparkGuidesDialog from "@/components/scribe/UnreviewedSparkGuidesDialog.vue";
	import { useScribeStore } from "@/stores/scribe";
	import { useRunAgentOrchestrator } from "@/components/scribe/composables/useRunAgentOrchestrator";
	import { useToast } from "vue-toastification";
	import type { KnowledgeAssignment, KnowledgeRequirement, PackKnowledgeFulfillment, PackWithExecuting } from "@/components/scribe/types";
	import type { AnalysisStatusResponseData } from "@/types/scribe/Analysis";
	import { isFetchingReport } from "@/components/scribe/utils/analysisStatus";
	import { fulfillPackRequirements, getPackAssignments, getPackRequirements, markLocalKnowledgeReviewed } from "@/components/scribe/api";
	import { filterReviewableRequirements } from "@/components/scribe/knowledgeRequirements";
	import { couldNot, genericFailure } from "@/helpers/userMessages";

	const toast = useToast();
	const { smAndDown, xs, mobile } = useDisplay();

	const isMobileView = computed(() => {
		return smAndDown.value || mobile.value;
	});

	const props = withDefaults(
		defineProps<{
			location?: "corner-page";
			type: "project" | "conversation";
			id: string;
			disabled?: boolean;
			mediaProcessing?: boolean;
			isVisible?: boolean;
			suppressAutoTooltip?: boolean;
			freeTrialEnded?: boolean;
			selectedFileIds?: string[];
			isExternalEditor?: boolean;
		}>(),
		{
			location: "corner-page",
			disabled: false,
			mediaProcessing: false,
			isVisible: true,
			suppressAutoTooltip: false,
			freeTrialEnded: false,
			selectedFileIds: () => [],
			isExternalEditor: false
		}
	);

	const emit = defineEmits<{
		"request-upgrade": [];
		"analysis-created": [analysisId: string];
		// LSP-20159: a project run was committed and its create request is in flight.
		"analysis-starting": [];
	}>();

	const store = useScribeStore();

	const shouldShowFetchingAnalysis = (analysis: Pick<AnalysisStatusResponseData, "status" | "deferredMedia">) => {
		return isFetchingReport(analysis.status, analysis.deferredMedia);
	};

	const showRunning = computed(() => {
		return !!runningAnalysis.value;
	});

	// LSP-16333: CANCELLING is not "fetching" — once the user cancels, the popup
	// must stop showing "Running Your Spark...".
	const runningAnalysis = computed(() => {
		return store.currentAnalyses.find(shouldShowFetchingAnalysis);
	});

	// Orchestrator
	const selectedFileIdsRef = computed(() => props.selectedFileIds ?? []);
	const orchestrator = useRunAgentOrchestrator({
		type: props.type,
		id: toRef(props, "id"),
		selectedFileIds: selectedFileIdsRef,
		isExternalEditor: toRef(props, "isExternalEditor")
	});

	// Popup and dialog visibility
	const showPopup = ref(false);
	const showDialog = ref(false);
	const showUnreviewedGuidesDialog = ref(false);
	const showSparkGuidesWizard = ref(false);
	const guideRequirements = ref<KnowledgeRequirement[]>([]);
	const guideAssignments = ref<KnowledgeAssignment[]>([]);
	const guideReviewLoading = ref(false);
	const guideReviewRunId = ref(0);
	const savedGuideReviewFulfillmentKeys = new Set<string>();
	const dismissed = ref(false);

	const handleFabClick = () => {
		closeAutoTooltip();
		if (props.freeTrialEnded) {
			emit("request-upgrade");
			return;
		}
		if (showPopup.value) {
			showPopup.value = false;
		} else {
			showPopup.value = true;
		}
	};

	const handleDismiss = () => {
		closeAutoTooltip();
		showPopup.value = false;
		dismissed.value = true;
	};

	const openPopup = () => {
		dismissed.value = false;
		closeAutoTooltip();
		if (props.freeTrialEnded) {
			emit("request-upgrade");
			return;
		}
		// Defer to next event loop so the current click finishes bubbling
		// before showPopup is true — otherwise handleClickOutside closes it immediately
		setTimeout(() => {
			showPopup.value = true;
		}, 0);
	};

	defineExpose({ openPopup });

	const continueWithLoadedPack = async () => {
		if (orchestrator.needsDialog.value) {
			showPopup.value = false;
			showDialog.value = true;
		} else {
			// LSP-20159: a project's create request waits on Scribe for seconds (p99 21s). Close the popup
			// and move to Sparky's Reports as soon as the run is committed, where the "Starting your
			// report…" placeholder is already showing. The orchestrator is deliberately NOT reset until
			// the run settles — its runningAnalysis flag is what blocks a second click from a second POST.
			let dispatchStarted = false;
			const success = await orchestrator.runAnalysis({
				onDispatchStarted: () => {
					dispatchStarted = true;
					showPopup.value = false;
					emit("analysis-starting");
				}
			});
			showPopup.value = false;
			if (success) {
				orchestrator.reset();
				// Already on the reports tab since dispatch; a late second switch would yank a user who moved on.
				if (!dispatchStarted) {
					emit("analysis-created", "direct");
				}
			}
		}
	};

	const selectedPackNeedsSparkGuideReview = computed(() => {
		// LSP-18409: on a cross-org shared project the pack lives in the OWNER's tenant. The client
		// Spark-Guide calls (getPackRequirements / getPackAssignments / markLocalKnowledgeReviewed) run
		// under the CALLER's token against the owner's pack + organizationInstalledPackId, so they fail or
		// leave the review dialog stuck. Guide review belongs to the owner and is enforced under the owner
		// tenant at run time, so a cross-org editor skips the client-side gate and runs directly.
		if (props.isExternalEditor) return false;
		const pack = orchestrator.selectedPack.value;
		return Boolean(
			pack?.organizationInstalledPackId &&
			pack?.hasKnowledgeRequirements &&
			(pack.localKnowledgeReviewRequired || !pack.localKnowledgeReviewedAt)
		);
	});

	const updateSelectedPackGuideReviewState = (localKnowledgeReviewedAt: string) => {
		const pack = orchestrator.selectedPack.value;
		if (!pack) return;
		pack.localKnowledgeReviewedAt = localKnowledgeReviewedAt;
		pack.localKnowledgeReviewRequired = false;
	};

	const markSelectedPackGuidesReviewed = async (): Promise<void> => {
		const pack = orchestrator.selectedPack.value;
		const organizationInstalledPackId = pack?.organizationInstalledPackId;
		if (!organizationInstalledPackId) {
			console.warn("markSelectedPackGuidesReviewed: selected pack has no organizationInstalledPackId", { packId: pack?.id });
			return;
		}

		const result = await markLocalKnowledgeReviewed(organizationInstalledPackId);
		updateSelectedPackGuideReviewState(result.localKnowledgeReviewedAt);
	};

	const assignmentCustomizesReviewableGuide = (assignment: KnowledgeAssignment, reviewableRequirementIds: Set<string>) => {
		return !assignment.useDefaultData && reviewableRequirementIds.has(assignment.taskKnowledgeRequirementId);
	};

	const markSelectedPackReviewedIfAnyGuideIsCustomized = async (
		pack: PackWithExecuting,
		organizationInstalledPackId: string
	): Promise<boolean> => {
		try {
			const [requirementsResult, assignmentsResult] = await Promise.all([
				getPackRequirements(pack.id),
				getPackAssignments(organizationInstalledPackId)
			]);
			const reviewableRequirementIds = new Set(
				filterReviewableRequirements(requirementsResult.data).map((requirement) => requirement.id)
			);
			const hasCustomizedGuide = assignmentsResult.data.some((assignment) =>
				assignmentCustomizesReviewableGuide(assignment, reviewableRequirementIds)
			);

			if (!hasCustomizedGuide) {
				return false;
			}

			const result = await markLocalKnowledgeReviewed(organizationInstalledPackId);
			const selectedPack = orchestrator.selectedPack.value;
			if (selectedPack?.organizationInstalledPackId === organizationInstalledPackId) {
				updateSelectedPackGuideReviewState(result.localKnowledgeReviewedAt);
			}
			return true;
		} catch (error) {
			console.warn("Unable to infer Spark Guide review state from customized assignments:", {
				packId: pack.id,
				organizationInstalledPackId,
				error
			});
			return false;
		}
	};

	const guideReviewFulfillmentKey = (fulfillment: PackKnowledgeFulfillment): string => {
		if (fulfillment.useDefaultData) {
			return `${fulfillment.requirementId}|${fulfillment.knowledgeType}|default`;
		}

		if (fulfillment.knowledgeType === "table") {
			const columnMapping = Object.entries(fulfillment.tableColumnMapping ?? {}).sort(([left], [right]) => left.localeCompare(right));
			return `${fulfillment.requirementId}|table|${fulfillment.tableDataS3Key}|${JSON.stringify(columnMapping)}`;
		}

		return `${fulfillment.requirementId}|document|${fulfillment.existingKnowledgeDocumentId}`;
	};

	const resetSavedGuideReviewFulfillments = () => {
		savedGuideReviewFulfillmentKeys.clear();
	};

	const rememberSavedGuideReviewFulfillments = (fulfillments: PackKnowledgeFulfillment[]) => {
		fulfillments.forEach((fulfillment) => {
			savedGuideReviewFulfillmentKeys.add(guideReviewFulfillmentKey(fulfillment));
		});
	};

	const getUnsavedGuideReviewFulfillments = (fulfillments: PackKnowledgeFulfillment[]) => {
		return fulfillments.filter((fulfillment) => !savedGuideReviewFulfillmentKeys.has(guideReviewFulfillmentKey(fulfillment)));
	};

	const handlePackSelected = async (pack: PackWithExecuting) => {
		await orchestrator.loadPackDetails(pack);

		// If loadPackDetails cleared selectedPack (e.g. deleted pack, no assets), bail
		if (!orchestrator.selectedPack.value) {
			if (orchestrator.error.value) {
				toast.error(orchestrator.error.value);
				orchestrator.reset();
			}
			return;
		}

		if (selectedPackNeedsSparkGuideReview.value) {
			const selectedPack = orchestrator.selectedPack.value;
			const organizationInstalledPackId = selectedPack?.organizationInstalledPackId;
			if (selectedPack && organizationInstalledPackId) {
				const reviewedByCustomizedGuide = await markSelectedPackReviewedIfAnyGuideIsCustomized(
					selectedPack,
					organizationInstalledPackId
				);
				if (orchestrator.selectedPack.value?.id !== selectedPack.id) {
					return;
				}
				if (reviewedByCustomizedGuide) {
					await continueWithLoadedPack();
					return;
				}
			}

			showPopup.value = false;
			showUnreviewedGuidesDialog.value = true;
			return;
		}

		await continueWithLoadedPack();
	};

	const handleUnreviewedGuidesCancel = () => {
		if (guideReviewLoading.value) return;
		showUnreviewedGuidesDialog.value = false;
		orchestrator.reset();
	};

	const handleUnreviewedGuidesContinue = async ({ markReviewed }: { markReviewed: boolean }) => {
		if (guideReviewLoading.value) return;

		guideReviewLoading.value = true;
		try {
			if (markReviewed) {
				await markSelectedPackGuidesReviewed();
			}
			showUnreviewedGuidesDialog.value = false;
			await continueWithLoadedPack();
		} catch (error) {
			console.error("Failed to continue with unreviewed Spark Guides:", error);
			toast.error(genericFailure());
		} finally {
			guideReviewLoading.value = false;
		}
	};

	const handleUnreviewedGuidesReview = async () => {
		if (guideReviewLoading.value) return;

		resetSavedGuideReviewFulfillments();

		const pack = orchestrator.selectedPack.value;
		const organizationInstalledPackId = pack?.organizationInstalledPackId;
		if (!pack || !organizationInstalledPackId) {
			console.warn("handleUnreviewedGuidesReview: selected pack missing or not installed", { packId: pack?.id });
			toast.error(couldNot("load Spark Guides"));
			return;
		}

		guideReviewLoading.value = true;
		try {
			const [requirementsResult, assignmentsResult] = await Promise.all([
				getPackRequirements(pack.id),
				getPackAssignments(organizationInstalledPackId)
			]);
			const requirements = filterReviewableRequirements(requirementsResult.data);
			if (requirements.length === 0) {
				// The pack is flagged as needing review but returned no reviewable guides —
				// a wizard with zero steps can never finish, so mark the review complete
				// before continuing to avoid prompting on every future run.
				console.warn("handleUnreviewedGuidesReview: pack flagged for review but has no Spark Guide requirements", {
					packId: pack.id,
					organizationInstalledPackId
				});
				try {
					await markSelectedPackGuidesReviewed();
				} catch (error) {
					console.error("Failed to mark empty Spark Guide review complete:", {
						packId: pack.id,
						organizationInstalledPackId,
						error
					});
					toast.error(couldNot("mark the Spark Guides as reviewed"));
					return;
				}
				showUnreviewedGuidesDialog.value = false;
				await continueWithLoadedPack();
				return;
			}
			guideRequirements.value = requirements;
			guideAssignments.value = assignmentsResult.data;
			showUnreviewedGuidesDialog.value = false;
			showSparkGuidesWizard.value = true;
		} catch (error) {
			console.error("Failed to load Spark Guides for review:", error);
			toast.error(couldNot("load Spark Guides"));
		} finally {
			guideReviewLoading.value = false;
		}
	};

	const closeSparkGuidesWizard = () => {
		showSparkGuidesWizard.value = false;
		guideRequirements.value = [];
		guideAssignments.value = [];
		resetSavedGuideReviewFulfillments();
	};

	const handleSparkGuidesWizardCancel = () => {
		if (guideReviewLoading.value) return;
		guideReviewRunId.value += 1;
		closeSparkGuidesWizard();
		orchestrator.reset();
	};

	const handleSparkGuidesWizardFinish = async (fulfillments: PackKnowledgeFulfillment[]) => {
		if (guideReviewLoading.value) return;

		const pack = orchestrator.selectedPack.value;
		const organizationInstalledPackId = pack?.organizationInstalledPackId;
		if (!organizationInstalledPackId) {
			console.warn("handleSparkGuidesWizardFinish: selected pack has no organizationInstalledPackId", { packId: pack?.id });
			toast.error(couldNot("save the Spark Guide review"));
			handleSparkGuidesWizardCancel();
			return;
		}

		guideReviewLoading.value = true;
		const runId = ++guideReviewRunId.value;
		const packId = pack.id;
		try {
			const fulfillmentsToSave = getUnsavedGuideReviewFulfillments(fulfillments);
			if (fulfillmentsToSave.length > 0) {
				try {
					await fulfillPackRequirements(organizationInstalledPackId, fulfillmentsToSave);
					rememberSavedGuideReviewFulfillments(fulfillmentsToSave);
				} catch (error) {
					console.error("Failed to save Spark Guide fulfillments:", {
						organizationInstalledPackId,
						fulfillmentCount: fulfillmentsToSave.length,
						error
					});
					toast.error(couldNot("save your Spark Guide changes"));
					return;
				}
			}

			try {
				await markSelectedPackGuidesReviewed();
			} catch (error) {
				const fulfillmentsSaved = fulfillments.length > 0 || savedGuideReviewFulfillmentKeys.size > 0;
				console.error("Failed to mark Spark Guides reviewed:", {
					organizationInstalledPackId,
					fulfillmentsSaved,
					error
				});
				toast.error(
					fulfillmentsSaved
						? "Spark Guide changes were saved, but we couldn't finish marking them reviewed. Please try again."
						: "Something went wrong marking Spark Guides reviewed. Please try again."
				);
				return;
			}

			closeSparkGuidesWizard();
			if (guideReviewRunId.value !== runId || orchestrator.selectedPack.value?.id !== packId) return;
			await continueWithLoadedPack();
		} catch (error) {
			console.error("Failed to save Spark Guide review:", error);
			toast.error(couldNot("save the Spark Guide review"));
		} finally {
			if (guideReviewRunId.value === runId) {
				guideReviewLoading.value = false;
			}
		}
	};

	const handleDialogClose = (value: boolean) => {
		showDialog.value = value;
		if (!value) {
			orchestrator.reset();
		}
	};

	const handleAnalysisCreated = (analysisId: string) => {
		orchestrator.reset();
		emit("analysis-created", analysisId);
	};

	// Click outside popup to close
	const handleClickOutside = (event: MouseEvent) => {
		if (!showPopup.value) return;
		const target = event.target as HTMLElement;
		// Don't close if clicking the FAB itself or inside the popup
		if (target.closest(".sean-fab-container")) return;
		// Don't close if clicking inside a Vuetify overlay (the dialog handles its own close)
		if (target.closest(".v-overlay")) return;
		showPopup.value = false;
	};

	// Auto tooltip
	const autoTooltipEnabled = ref(false);

	const closeAutoTooltip = () => {
		autoTooltipEnabled.value = false;
	};

	const showAutoTooltip = computed(
		() =>
			autoTooltipEnabled.value &&
			!dismissed.value &&
			!showPopup.value &&
			!showDialog.value &&
			!showUnreviewedGuidesDialog.value &&
			!showSparkGuidesWizard.value &&
			!props.suppressAutoTooltip
	);

	const fabSize = computed(() => {
		return smAndDown.value ? 64 : 128;
	});

	onMounted(() => {
		setTimeout(() => {
			autoTooltipEnabled.value = true;
		});
		document.addEventListener("click", handleClickOutside);
	});

	onBeforeUnmount(() => {
		document.removeEventListener("click", handleClickOutside);
	});
</script>
<template>
	<div
		v-if="isVisible"
		:class="{
			'sean-panel': true,
			'corner-page': location === 'corner-page',
			'breakpoint--xs': xs,
			mobile: isMobileView
		}">
		<div class="sean-fab-container">
			<v-btn v-show="!dismissed" elevation="0" icon class="sean-fab" :width="fabSize" :height="fabSize" @click="handleFabClick">
				<SeanIcon :height="fabSize" :width="fabSize" :is-running="showRunning" :enable-tooltip="false" />
			</v-btn>

			<!-- Dismiss button -->
			<v-btn
				v-if="!dismissed && !showPopup && !showDialog"
				icon
				variant="flat"
				color="grey-lighten-2"
				class="sean-dismiss-btn"
				@click.stop="handleDismiss">
				<v-icon size="10" color="grey-darken-1">mdi-close</v-icon>
			</v-btn>

			<!-- Chat-like popup -->
			<LuckyPackPopup
				:model-value="showPopup"
				:type="props.type"
				:id="props.id"
				:is-external-editor="props.isExternalEditor"
				:disabled="props.disabled"
				:media-processing="props.mediaProcessing"
				:loading-pack-id="
					orchestrator.loadingPack.value || orchestrator.runningAnalysis.value
						? (orchestrator.selectedPack.value?.id ?? null)
						: null
				"
				:running-analysis="runningAnalysis"
				:media-warnings="orchestrator.mediaWarnings.value"
				@update:model-value="showPopup = $event"
				@pack-selected="handlePackSelected" />

			<transition name="sean-tooltip-fade">
				<div v-if="showAutoTooltip" class="sean-tooltip-bubble" role="status" elevation="2" rounded="xl">
					<span v-if="!showRunning" class="tooltip-text">Hi, I'm Sparky. Click me to run a Spark on this session.</span>
					<span v-else class="tooltip-text"
						>I'm running your Spark. This will take a few minutes. You can leave this page and come back later! Your report will
						be available in the "Sparky's Reports" section.</span
					>
					<v-btn
						icon="icon-close-medium"
						size="small"
						density="compact"
						color="grey-lighten-3"
						variant="text"
						@mousedown.stop.prevent="closeAutoTooltip">
					</v-btn>
					<span class="tooltip-arrow" />
				</div>
			</transition>
		</div>

		<RunAgentDialog
			:model-value="showDialog"
			:orchestrator="orchestrator"
			@update:model-value="handleDialogClose"
			@analysis-created="handleAnalysisCreated" />
		<UnreviewedSparkGuidesDialog
			v-model="showUnreviewedGuidesDialog"
			:loading="guideReviewLoading"
			@cancel="handleUnreviewedGuidesCancel"
			@review="handleUnreviewedGuidesReview"
			@continue="handleUnreviewedGuidesContinue" />
		<SparkGuidesWizard
			v-model="showSparkGuidesWizard"
			:requirements="guideRequirements"
			:assignments="guideAssignments"
			:include-default-fulfillments="false"
			:installing="guideReviewLoading"
			@cancel="handleSparkGuidesWizardCancel"
			@finish="handleSparkGuidesWizardFinish" />
	</div>
</template>
<style>
	.sean-panel {
		z-index: 200 !important;
	}
	.sean-panel.corner-page {
		position: fixed;
		right: calc(12px + env(safe-area-inset-right, 0px));
		bottom: calc(12px + env(safe-area-inset-bottom, 0px));
	}
	.mobile .sean-panel.corner-page,
	.sean-panel.corner-page.mobile {
		bottom: calc(68px + env(safe-area-inset-bottom, 0px));
	}
	.sean-max-view-height {
		max-height: 62vh !important;
	}
	.sean-max-view-height h4 {
		font-size: 1.25rem;
	}
	.sean-max-view-height .v-card__title .text-body-2 {
		word-break: break-word;
	}

	.sean-panel .v-btn {
		transition:
			width 0.2s ease-in-out,
			height 0.2s ease-in-out;
	}
	.sean-fab,
	.sean-fab:hover,
	.sean-fab .v-btn__content,
	.sean-fab .v-btn__content:hover {
		background-color: transparent !important;
		box-shadow: none !important;
	}

	.sean-fab-container {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.sean-dismiss-btn {
		position: absolute;
		top: 0;
		right: 0;
		z-index: 1;
		width: 20px !important;
		height: 20px !important;
		min-width: 20px !important;
		border-radius: 50% !important;
	}

	.sean-tooltip-bubble {
		position: absolute;
		bottom: calc(100% + 12px);
		right: 0;
		left: auto;
		transform: none;
		background: #2b2b2b;
		color: #fff;
		padding: 12px 16px;
		border-radius: 8px;
		width: clamp(300px, 70vw, 300px);
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
		display: flex;
		align-items: flex-start;
		gap: 8px;
		z-index: 300;
	}

	.tooltip-text {
		flex: 1;
		font-size: 14px;
		line-height: 1.4;
	}

	.tooltip-arrow {
		position: absolute;
		bottom: -6px;
		right: 24px;
		left: auto;
		transform: none;
		width: 12px;
		height: 12px;
		background: #2b2b2b;
		clip-path: polygon(50% 100%, 0 0, 100% 0);
	}

	.sean-tooltip-fade-enter-active,
	.sean-tooltip-fade-leave-active {
		transition: opacity 0.2s ease;
	}

	.sean-tooltip-fade-enter-from,
	.sean-tooltip-fade-leave-to {
		opacity: 0;
	}

	/* mobile landscape: hide the FAB panel on small viewports in landscape */
	@media (max-width: 599px) and (orientation: landscape) {
		.sean-panel.mobile {
			display: none;
		}
	}
</style>
